
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';

export default function Home() {
  const [scrollY, setScrollY] = useState(0);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisibleSections(prev => new Set(prev).add(entry.target.id));
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const minerals = [
    {
      icon: 'ri-fire-line',
      name: 'Coal',
      description: 'Export-grade thermal coal near Richards Bay for international markets',
      color: '#0B1D2E'
    },
    {
      icon: 'ri-copper-coin-line',
      name: 'Chrome',
      description: 'High-purity chrome ore production in the North West region',
      color: '#2DD4BF'
    },
    {
      icon: 'ri-hammer-line',
      name: 'Iron Ore',
      description: 'Emerging iron ore assets and specialized contracting services',
      color: '#14B8A6'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/6431dad772d1fbd1ffd4f9621f57fdd9.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1D2E]/80 via-[#0B1D2E]/60 to-transparent"></div>
        
        <div 
          className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-left w-full"
          style={{ transform: `translateY(${scrollY * 0.3}px)` }}
        >
          <div className="max-w-3xl">
            <h1 
              className="text-5xl md:text-7xl font-bold text-[#F9F9F9] mb-6 leading-tight animate-fade-in-up"
              style={{ fontFamily: '"Montserrat", sans-serif' }}
            >
              Pure. Natural. Essential.
            </h1>
            <p 
              className="text-xl md:text-2xl text-[#E9E3D0] mb-4 leading-relaxed animate-fade-in-up animation-delay-300"
              style={{ fontFamily: '"Open Sans", sans-serif' }}
            >
              A 100% Black youth- and woman-owned mining company driving sustainable progress across South Africa's mineral landscape.
            </p>
            <p 
              className="text-lg text-[#2DD4BF] mb-8 font-medium animate-fade-in-up animation-delay-500"
              style={{ fontFamily: '"Open Sans", sans-serif' }}
            >
              Subsidiary of Segra Group • Founded 2025
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-700">
              <Button 
                size="lg" 
                href="/operations"
                className="bg-[#2DD4BF] text-[#0B1D2E] hover:bg-[#14B8A6] hover:transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{ fontFamily: '"Montserrat", sans-serif' }}
              >
                Explore Operations
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                href="/contact"
                className="border-2 border-[#2DD4BF] text-[#2DD4BF] hover:bg-[#2DD4BF] hover:text-[#0B1D2E] hover:transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{ fontFamily: '"Open Sans", sans-serif' }}
              >
                Contact Us
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-[#2DD4BF] rounded-full flex justify-center">
            <div className="w-1 h-3 bg-[#2DD4BF] rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Company Introduction */}
      <section className="py-20 bg-[#F9F9F9]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div 
              id="intro-text"
              data-animate
              className={`transition-all duration-1000 ease-out ${
                visibleSections.has('intro-text') 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 -translate-x-10'
              }`}
            >
              <h2 
                className="text-4xl md:text-5xl font-bold text-[#0B1D2E] mb-6"
                style={{ fontFamily: '"Montserrat", sans-serif' }}
              >
                Forged in Resilience. Driven by Opportunity.
              </h2>
              <p 
                className="text-lg text-[#0B1D2E] mb-6 leading-relaxed"
                style={{ fontFamily: '"Open Sans", sans-serif' }}
              >
                Hera Minerals represents a new generation of mining excellence in Africa. As a 100% Black youth- and woman-owned company, we combine transformative ownership with operational excellence to extract and produce minerals that fuel progress and improve lives.
              </p>
              <p 
                className="text-lg text-[#0B1D2E] mb-8 leading-relaxed"
                style={{ fontFamily: '"Open Sans", sans-serif' }}
              >
                Our commitment extends beyond extraction – we prioritize safety, environmental stewardship, and community development in every operation across South Africa's mineral-rich landscape.
              </p>
              <Button 
                href="/about"
                className="bg-[#2DD4BF] text-[#0B1D2E] hover:bg-[#14B8A6] hover:transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
                style={{ fontFamily: '"Montserrat", sans-serif' }}
              >
                Learn Our Story
              </Button>
            </div>
            <div 
              id="intro-image"
              data-animate
              className={`relative transition-all duration-1000 ease-out delay-300 ${
                visibleSections.has('intro-image') 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 translate-x-10'
              }`}
            >
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20African%20mining%20team%2C%20diverse%20group%20of%20young%20professionals%20and%20women%20in%20mining%20safety%20gear%2C%20modern%20mining%20facility%20background%2C%20corporate%20photography%2C%20confident%20and%20empowered%20team%2C%20South%20African%20mining%20industry%2C%20premium%20quality%20image&width=600&height=400&seq=team-mining&orientation=landscape"
                alt="Hera Minerals Team"
                className="rounded-2xl shadow-2xl object-cover w-full h-96"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#2DD4BF] text-[#0B1E2E] p-6 rounded-xl shadow-xl">
                <p className="text-2xl font-bold" style={{ fontFamily: '"Montserrat", sans-serif' }}>100%</p>
                <p className="text-sm font-medium" style={{ fontFamily: '"Open Sans", sans-serif' }}>Black Youth & Woman Owned</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Minerals Section */}
      <section className="py-20 bg-[#E9E3D0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div 
            id="minerals-header"
            data-animate
            className={`text-center mb-16 transition-all duration-1000 ease-out ${
              visibleSections.has('minerals-header') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <h2 
              className="text-4xl md:text-5xl font-bold text-[#0B1D2E] mb-6"
              style={{ fontFamily: '"Montserrat", sans-serif' }}
            >
              Our Mineral Portfolio
            </h2>
            <p 
              className="text-xl text-[#0B1D2E] max-w-3xl mx-auto"
              style={{ fontFamily: '"Open Sans", sans-serif' }}
            >
              Diversified operations across South Africa's most valuable mineral sectors, 
              delivering consistent quality and sustainable growth.
            </p>
          </div>

          <div 
            id="minerals-grid"
            data-animate
            className={`grid grid-cols-1 md:grid-cols-3 gap-8 transition-all duration-1000 ease-out delay-300 ${
              visibleSections.has('minerals-grid') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            {minerals.map((mineral, index) => (
              <div 
                key={mineral.name}
                className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 group cursor-pointer transform hover:scale-105"
                style={{ animationDelay: `${index * 200}ms` }}
              >
                <div 
                  className="w-16 h-16 flex items-center justify-center rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300"
                  style={{ backgroundColor: mineral.color }}
                >
                  <i className={`${mineral.icon} text-2xl text-white`}></i>
                </div>
                <h3 
                  className="text-2xl font-bold text-[#0B1D2E] mb-4"
                  style={{ fontFamily: '"Montserrat", sans-serif' }}
                >
                  {mineral.name}
                </h3>
                <p 
                  className="text-[#0B1D2E] leading-relaxed"
                  style={{ fontFamily: '"Open Sans", sans-serif' }}
                >
                  {mineral.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Growth Vision */}
      <section className="py-20 bg-[#0B1D2E] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20mining%20patterns%2C%20geometric%20shapes%20representing%20growth%20and%20expansion%2C%20modern%20industrial%20design%2C%20metallic%20textures%2C%20sophisticated%20background%20pattern%2C%20corporate%20design%20elements%2C%20premium%20quality&width=1920&height=600&seq=growth-pattern&orientation=landscape"
            alt="Growth Pattern"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div 
            id="growth-header"
            data-animate
            className={`text-center mb-16 transition-all duration-1000 ease-out ${
              visibleSections.has('growth-header') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <h2 
              className="text-4xl md:text-5xl font-bold text-[#2DD4BF] mb-6"
              style={{ fontFamily: '"Montserrat", sans-serif' }}
            >
              Future Growth Targets
            </h2>
            <p 
              className="text-xl text-[#E9E3D0] max-w-3xl mx-auto"
              style={{ fontFamily: '"Open Sans", sans-serif' }}
            >
              Ambitious expansion plans backed by strategic investments in infrastructure and technology.
            </p>
          </div>

          <div 
            id="growth-stats"
            data-animate
            className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 transition-all duration-1000 ease-out delay-300 ${
              visibleSections.has('growth-stats') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-[#2DD4BF]/20 hover:border-[#2DD4BF]/50 transition-all duration-500 hover:transform hover:scale-105">
              <div className="text-center">
                <div className="text-4xl font-bold text-[#2DD4BF] mb-2" style={{ fontFamily: '"Montserrat", sans-serif' }}>
                  200,000
                </div>
                <div className="text-lg text-[#E9E3D0] mb-4" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                  tons/month Coal Target
                </div>
                <div className="text-sm text-[#2DD4BF]" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                  From 0,000 current capacity
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-[#2DD4BF]/20 hover:border-[#2DD4BF]/50 transition-all duration-500 hover:transform hover:scale-105">
              <div className="text-center">
                <div className="text-4xl font-bold text-[#2DD4BF] mb-2" style={{ fontFamily: '"Montserrat", sans-serif' }}>
                  50,000
                </div>
                <div className="text-lg text-[#E9E3D0] mb-4" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                  tons/month Chrome Target
                </div>
                <div className="text-sm text-[#2DD4BF]" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                  High-purity production
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-[#2DD4BF]/20 hover:border-[#2DD4BF]/50 transition-all duration-500 hover:transform hover:scale-105 md:col-span-2 lg:col-span-1">
              <div className="text-center">
                <div className="text-2xl font-bold text-[#2DD4BF] mb-2" style={{ fontFamily: '"Montserrat", sans-serif' }}>
                  Infrastructure
                </div>
                <div className="text-lg text-[#E9E3D0] mb-4" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                  Wash Plants & Logistics
                </div>
                <div className="text-sm text-[#2DD4BF]" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                  Strategic investments
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2DD4BF]">
        <div 
          id="cta-section"
          data-animate
          className={`max-w-4xl mx-auto px-6 lg:px-8 text-center transition-all duration-1000 ease-out ${
            visibleSections.has('cta-section') 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 
            className="text-4xl md:text-5xl font-bold text-[#0B1D2E] mb-6"
            style={{ fontFamily: '"Montserrat", sans-serif' }}
          >
            Ready to Get in Touch?
          </h2>
          <p 
            className="text-xl text-[#0B1D2E] mb-8"
            style={{ fontFamily: '"Open Sans", sans-serif' }}
          >
            Discover how Hera Minerals can contribute to your success
          </p>
          <div className="flex justify-center">
            <Button 
              variant="secondary" 
              size="lg" 
              href="/contact" 
              className="bg-[#0B1D2E] text-white hover:bg-[#0B1D2E]/90 hover:transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{ fontFamily: '"Montserrat", sans-serif' }}
            >
              Get in Touch
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
